import React, { useContext, useEffect, useRef, useState } from 'react'
import { loadMoov } from '@moovio/moov-js'
import CustomWrapper from '../../wrapper/CustomWrapper'
import PopupWrapper from '../../wrapper/PopupWrapper'
import Button from '../../Button/Button'
import image from "../../../assets/checkinAccount.jpeg"
import ErrorPng from "../../../assets/error.png";
import { FormException } from '../../../utility/exceptions'
import { addPaymentMethod, createCardSession, deletePaymentMethod, updatePaymentMethod } from '../../../services/payment.method.service'
import { MdDelete } from 'react-icons/md'
import { AppContext } from '../../../App'

function PaymentMethod({ title = "", userData = null, pageNavigator = null, isNew = true, data = null, edit = false }) {
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isDeleteCalled, setIsDeleteCalled] = useState(false);
  const [error, setError] = useState(false);
  // Primary flag for the new-card (Moov Drops) path, which has no raw form fields.
  const [isPrimary, setIsPrimary] = useState(false);
  // Guards against re-initializing Moov.js on re-renders.
  const moovDropRef = useRef(false);
  // Refs to the declarative Moov web components. <moov-form> owns tokenization +
  // submission (requestSubmit); the three inputs are each their own PCI iframe
  // and register to the form via their `formname` attribute. We use the
  // declarative elements (not imperative drops.form()) because the imperative
  // call never inserts a queryable <moov-form> element into the DOM.
  const moovFormRef = useRef(null);
  const cardNumberRef = useRef(null);
  const cardExpiryRef = useRef(null);
  const cardCvcRef = useRef(null);
  // Disables Submit until Moov.js has initialized the form element.
  const [moovFormReady, setMoovFormReady] = useState(false);
  // The current Moov card-session token and when it lapses (epoch ms). The token
  // is short-lived; if the user lingers on the card form we refresh it before
  // submitting so tokenization never fails mid-entry.
  const moovTokenRef = useRef(null);
  const moovTokenExpiresAtRef = useRef(0);
  // Refresh the token when it is within this window of expiring.
  const TOKEN_REFRESH_WINDOW_MS = 60 * 1000;
  // The non-sensitive card details the user typed (name + billing ZIP), captured
  // at submit so the Moov onSuccess callback can persist them in our record — the
  // raw PAN/CVV/expiry stay inside Moov and never reach us.
  const cardMetaRef = useRef({ holderName: "", postalCode: "" });

  // Any brand-new card must be tokenized client-side via Moov.js Drops so the
  // backend receives a Moov cardID as token_type — this covers both the initial
  // "Setup a Payment method" flow (edit=false) and the "Add New Account" flow
  // (edit=true, where edit only drives the "set as primary" checkbox). Only
  // editing an existing card (isNew=false) keeps the legacy raw-input flow.
  const isNewCard = isNew;

  // Called once Moov's hosted form has created the card on the renter's Moov
  // account. We only forward the resulting cardID (as token_type); raw PAN/CVV
  // never leave Moov's iframes. The backend derives funding/brand/last4 via getCard.
  const handleCardLinked = async (card) => {
    let messageDiv = document.getElementsByClassName("message").item(0);
    const cardID = card?.cardID ?? card?.cardId ?? (typeof card === "string" ? card : null);
    if (!cardID) {
      return FormException(null, card?.error?.message || "Card could not be linked", messageDiv);
    }
    // Persist the cardholder name (and billing ZIP) in our detail so they display
    // when the card is later edited. Brand/last4 are still derived server-side via
    // Moov getCard; raw card data never lands here. detail stays double-base64
    // encoded to match the rest of the payment-method records.
    const detailPayload = {
      cardOwnerName: cardMetaRef.current.holderName || "",
      postalCode: cardMetaRef.current.postalCode || "",
    };
    const dataToSend = {
      detail: btoa(btoa(JSON.stringify(detailPayload))),
      isPrimary,
      payment_type: "card",
      token_type: cardID,
      status: "Active",
      user_id: userData?.data?.id,
      isForRenter: true,
    };
    const resp = await addPaymentMethod(dataToSend);
    if (resp?.status) {
      setPaymentMethod(null);
      pageNavigator("ManageAccount");
    } else {
      return FormException(null, resp.message, messageDiv);
    }
  };

  // Mount Moov's composable card Drops (a hidden <moov-form> plus the card
  // number / expiry / CVC inputs) when the user opens the new-card form. Each
  // sensitive field lives in its own Moov iframe, mirroring the old Stripe
  // CardNumberElement / CardExpiryElement / CardCvcElement layout 1:1. On the
  // app's Submit we call moov-form.requestSubmit() to POST the card to Moov.
  useEffect(() => {
    // Allow the Drops to re-initialize the next time the new-card form is opened.
    if (paymentMethod !== "B") {
      moovDropRef.current = false;
      moovTokenRef.current = null;
      moovTokenExpiresAtRef.current = 0;
      setMoovFormReady(false);
      return;
    }
    if (!isNewCard || moovDropRef.current) return;
    moovDropRef.current = true;

    let cancelled = false;
    (async () => {
      let messageDiv = document.getElementsByClassName("message").item(0);
      try {
        const resp = await createCardSession();
        if (cancelled) return;
        if (!resp?.status || !resp?.data?.token) {
          moovDropRef.current = false;
          return FormException(null, resp?.message || "Unable to start card session", messageDiv);
        }
        const { token, accountID, expiresOn } = resp.data;
        // Remember the token + expiry so submit can refresh it if it's about to
        // lapse. Fall back to a conservative 5-min lifetime if expiresOn is absent.
        moovTokenRef.current = token;
        moovTokenExpiresAtRef.current = expiresOn ? new Date(expiresOn).getTime() : Date.now() + 5 * 60 * 1000;

        // Text styling applied inside each Moov iframe so the hosted inputs
        // read like the app's other form fields.
        const inputStyle = {
          fontFamily: "Poppins, sans-serif",
          fontSize: "15px",
          color: "#000",
          backgroundColor: "transparent",
        };

        // Configure the declarative <moov-form> + inputs via their element refs.
        // Object/function props (requestHeaders, onSuccess, inputStyle, …) can't be
        // passed as JSX attributes, so we assign them on the elements before
        // Moov.js initializes them below. The elements register the controller
        // that backs requestSubmit() once initialized.
        const formEl = moovFormRef.current;
        if (formEl) {
          formEl.action = `/accounts/${accountID}/cards`;
          formEl.requestHeaders = {
            "content-type": "application/json",
            authorization: `Bearer ${token}`,
          };
          formEl.onSuccess = (response) => {
            Promise.resolve(response?.json ? response.json() : response)
              .then((card) => handleCardLinked(card))
              .catch(() => handleCardLinked(null));
          };
          formEl.onError = () => {
            FormException(null, "Card could not be linked", document.getElementsByClassName("message").item(0));
          };
        }
        [cardNumberRef, cardExpiryRef, cardCvcRef].forEach((r) => {
          if (r.current) {
            r.current.inputStyle = inputStyle;
            r.current.required = true;
          }
        });

        // loadMoov + drops() initialize Moov.js and fire the "moovJSLoaded" event
        // the custom elements listen for, wiring up their tokenization controllers.
        const moov = await loadMoov(token);
        if (cancelled) return;
        await moov.drops();
        if (cancelled) return;
        setMoovFormReady(true);
      } catch (e) {
        moovDropRef.current = false;
        FormException(null, e?.message || "Unable to load the secure card form", messageDiv);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  useEffect(() => {
    document.title = "Select Payment Method | Renter | Ando";
    setPaymentDetails(data);
    if (!isNew && edit) {
      if (data?.payment_type == "account") {
        setPaymentMethod("A");
      }
      else if (data?.payment_type == "card") {
        setPaymentMethod("B");
      }
    }
  }, []);

  const cancel = () => {
    if (!isNew && edit) {
      setPaymentMethod(null);
      pageNavigator("ManageAccount");
    }
    else {
      setPaymentMethod(null)
    }
  }

  // Submit handler for the new-card (Moov composable) flow. Mirrors the old
  // Stripe path (which called stripe.createPaymentMethod on submit): we push the
  // non-sensitive cardholder name into the form's requestBody, then ask Moov's
  // hosted <moov-form> to validate + POST the card. onSuccess (in the effect)
  // forwards the returned cardID to the backend.
  const submitMoovCard = async (e) => {
    e.preventDefault();
    let messageDiv = document.getElementsByClassName("message").item(0);
    // Capture the (non-sensitive) cardholder name + billing ZIP before any await,
    // so we don't depend on the event target after an async hop.
    const holderName = e.target.cardOwnerName?.value || "";
    const postalCode = (e.target.postalCode?.value || "").trim();
    // Moov requires billingAddress.postalCode to link a card (AVS) — there is no
    // way to disable it, unlike Stripe. Only the ZIP is mandatory.
    if (!postalCode) {
      return FormException(null, "Billing ZIP / postal code is required", messageDiv);
    }
    // Stash the non-sensitive fields so onSuccess can persist them in our record.
    cardMetaRef.current = { holderName, postalCode };
    const formEl = moovFormRef.current;
    if (!formEl || typeof formEl.requestSubmit !== "function") {
      return FormException(null, "Card form is still loading, please try again", messageDiv);
    }
    // If the Moov card-session token is at/near expiry, silently refresh it so
    // the card POST (which authenticates with this token) can't fail mid-entry.
    // The mounted card iframes keep the user's typed values; only the bearer used
    // for submission is swapped out.
    if (Date.now() >= moovTokenExpiresAtRef.current - TOKEN_REFRESH_WINDOW_MS) {
      const refreshed = await createCardSession();
      if (!refreshed?.status || !refreshed?.data?.token) {
        return FormException(null, refreshed?.message || "Secure session expired, please reopen the card form", messageDiv);
      }
      moovTokenRef.current = refreshed.data.token;
      moovTokenExpiresAtRef.current = refreshed.data.expiresOn
        ? new Date(refreshed.data.expiresOn).getTime()
        : Date.now() + 5 * 60 * 1000;
    }
    // holderName + billingAddress are not sensitive, so they ride along in the
    // request body; Moov merges them with the tokenized card fields on submit.
    formEl.requestBody = { holderName, billingAddress: { postalCode } };
    // Point the form at the freshest token for its POST to Moov.
    if (moovTokenRef.current) {
      formEl.requestHeaders = {
        "content-type": "application/json",
        authorization: `Bearer ${moovTokenRef.current}`,
      };
    }
    if (typeof formEl.requestSubmit === "function") {
      formEl.requestSubmit();
    } else {
      formEl.submit();
    }
  }

  const saveRenterPaymentDetails = async (e) => {
    e.preventDefault();
    let paymentType = e.target.attributes.getNamedItem("data-target").value;

    let messageDiv = document.getElementsByClassName("message").item(0);

    let dataToSend = {
      detail: null,
      isPrimary: e.target?.isPrimary?.checked ?? true,
      payment_type: paymentType,
      token_type: null,
      status: "Active",
      user_id: userData?.data?.id,
      isForRenter: true
    }
    if (dataToSend.payment_type == 'account') {
      if (e.target.confirmAccountNumber.value != e.target.accountNumber.value) {
        return FormException(e, "Account Number not maching to confirm account field", messageDiv);
      }
      dataToSend.detail = {
        accountNumber: e.target.accountNumber.value,
        routingNumber: e.target.routingNumber.value,
        accountNickname: e.target.accountNickname.value,
      }
      dataToSend.token_type = e.target.accountNumber.value;
    }
    else if (dataToSend.payment_type == 'card') {
      const cardNumber = e.target.cardNumber.value?.replace(/\s/g, "");
      if (cardNumber?.length < 13 || cardNumber?.length > 19) return FormException(e, "Invalid Card Number", messageDiv);
      // The card is tokenized/linked to the renter's Moov account on the backend.
      // Moov expects a 2-digit expiry year and returns the funding type (credit/debit),
      // brand and last4, which the backend stores back into `detail`.
      dataToSend.detail = {
        cardOwnerName: e.target.cardOwnerName.value,
        cardNumber: cardNumber,
        cvv: e.target.cvv.value,
        expiryMonth: e.target.expiryMonth.value,
        expiryYear: String(e.target.expiryYear.value).slice(-2),
      }
      // token_type is derived from Moov's cardID on the backend once linked.
      dataToSend.token_type = null;
    }

    if (!isNew && edit) {
      updatePaymentMethod(paymentDetails?.id, dataToSend)
        .then((resp) => {
          if (resp.status) {
            setPaymentMethod(null);
            pageNavigator("ManageAccount");
          }
          else {
            return FormException(null, resp.message, messageDiv);
          }
        })
    }
    else {      
      dataToSend.detail = btoa(btoa(JSON.stringify(dataToSend.detail)))      
      const resp = await addPaymentMethod(dataToSend)
      if(resp?.status){
        setPaymentMethod(null);
        pageNavigator("ManageAccount");
      }
      else {
        return FormException(null, resp.message, messageDiv);
      }
    }
    messageDiv.style.display = "none"
  }

  const deleteAccountMethod = () => {
    let messageDiv = document.getElementsByClassName("message").item(0);
    deletePaymentMethod(paymentDetails?.id)
      .then((resp) => {
        if (resp.status) {
          setPaymentDetails(null);
          pageNavigator("ManageAccount");
        }
        else {
          deleteAccountConfirm(false)
          if (resp.message.includes("Cannot")) {
            setError(true)
          }
          else {
            return FormException(null, resp.message, messageDiv);
          }
        }
      })
  }

  const handleInputChange = (e) => {
    let newData = {
      detail: null,
      isPrimary: false
    };
    if (paymentMethod == 'A') {
      newData.detail = {
        routingNumber: "",
        accountNickname: "",
        accountNumber: ""
      }
    }
    else {
      newData.detail = {
        cardOwnerName: "",
        cardNumber: "",
        expiryMonth: "",
        expiryYear: "",
      }
    }

    if (paymentDetails != null) {
      newData = Object.create(paymentDetails);
    }

    switch (e.target.name) {
      case "routingNumber":
        if (!isNew)
          newData.detail.routingNumber = e.target.value
        break;

      case "accountNumber":
        if (!isNew)
          newData.detail.accountNumber = e.target.value
        break;

      case "accountNickname":
        if (!isNew)
          newData.detail.accountNickname = e.target.value
        break;

      case "cardOwnerName":
        if (!isNew)
          newData.detail.cardOwnerName = e.target.value
        break;

      case "cardNumber":
        if (!isNew)
          newData.detail.cardNumber = e.target.value
        break;

      case "expiryMonth":
        if (!isNew)
          newData.detail.expiryMonth = e.target.value
        break;

      case "expiryYear":
        if (!isNew)
          newData.detail.expiryYear = e.target.value
        break;
      case "isPrimary":
        // if(!isNew)
        newData.isPrimary = e.target.checked
        break;

    }
    setPaymentDetails(null);
    setPaymentDetails(newData)
  }

  const deleteAccountConfirm = (called) => {
    setIsDeleteCalled(called);
  }
  return (
    <CustomWrapper
      width='100%'
      maxWidth={2000}
      height='600px'
      maxHeight={"100%"}
      paddingHorizontal={30}
      paddingVertical={30}
    >
      <div className='paymentOptionContainer'>
        <div className='title'>{title}</div>
        <div className='columnFormInput'>
          <label className='formRadio'>
            <input type='radio' name='paymentMethod' value={"A"} onClick={() => { setPaymentMethod("A") }} />
            <span>Checking Account</span>
          </label>
          <label className='formRadio'>
            <input type='radio' name='paymentMethod' value={"B"} onClick={() => { setPaymentMethod("B") }} />
            <span>Credit or Debit</span>
          </label>
        </div>
      </div>
      {
        paymentMethod === "A" &&
        <PopupWrapper onClose={cancel}>
          <div className='title'>Add Personal Checking Account</div>
          <form onSubmit={saveRenterPaymentDetails} data-target='account'>
            <table className='formTable'>
              <tr>
                <td>Name on Account</td>
                <td>
                  <div className='formInput'>
                    <input
                      onChange={handleInputChange}
                      type='text'
                      name="accountNickname"
                      value={isNew ? null : paymentDetails?.detail.accountNickname}
                      required />
                  </div>
                </td>
              </tr>
              <tr>
                <td>Bank Routing Number</td>
                <td>
                  <div className='formInput'>
                    <input
                      onChange={handleInputChange}
                      type='text'
                      name="routingNumber"
                      value={isNew ? null : paymentDetails?.detail.routingNumber}
                      required />
                  </div>
                </td>
              </tr>
              <tr>
                <td>Checking Account Number</td>
                <td>
                  <div className='formInput'>
                    <input
                      onChange={handleInputChange}
                      type='text'
                      name="accountNumber"
                      value={isNew ? null : paymentDetails?.detail.accountNumber}
                      required />
                  </div>
                </td>
              </tr>
              <tr>
                <td>Confirm Checking Account Number</td>
                <td>
                  <div className='formInput'>
                    <input
                      onChange={handleInputChange}
                      type='text'
                      name="confirmAccountNumber"
                      required />
                  </div>
                </td>
              </tr>
            </table>
            <div className='rowFormInput'>
              {
                ((!isNew && edit) || (isNew && edit)) &&
                <label className='accountMinButton'>
                  <input type='checkbox' name='isPrimary' onChange={handleInputChange} checked={paymentDetails?.isPrimary} />
                  <span>Set as primary acccount</span>
                </label>
              }
            </div>
            <div className='message'>Sample Text</div>
            <div className="referenceImage">
              <img src={image} alt='Reference' />
            </div>
            {
              (!isNew && edit) &&
              <label className='accountMinButton' onClick={() => deleteAccountConfirm(true)}>
                <MdDelete color='red' size={15} />
                <label style={{ color: 'red' }}>Delete Account</label>
              </label>
            }
            <div className='buttonContainer'>
              <Button
                className='minButton'
                margin='-5px'
                type='button'
                primary={false}
                onPressFunction={cancel}
              >Cancel</Button>

              <Button
                className='minButton'
                margin='-5px'
                type='submit'
              >Submit</Button>
            </div>
          </form>
        </PopupWrapper>
      }
      {
        paymentMethod === "B" &&
        <PopupWrapper onClose={cancel}>
          <div className='title'>Add a Credit or Debit Card</div>
          <form onSubmit={isNewCard ? submitMoovCard : saveRenterPaymentDetails} data-target='card'>
            {isNewCard ? (
              <>
                {/* Same table layout as the legacy/Stripe card form. The three
                    sensitive fields are Moov composable Drops (each its own PCI
                    iframe) so raw card data is tokenized by Moov, not our app. */}
                <table className='formTable'>
                  <tr>
                    <td>Name on Card</td>
                    <td>
                      <div className='formInput'>
                        <input
                          type='text'
                          name="cardOwnerName"
                          placeholder='Name on Card'
                          required />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>Card Number</td>
                    <td>
                      <div className='formInput'>
                        <div className='fullWidth formInputMoov'>
                          <moov-card-number-input
                            ref={cardNumberRef}
                            name="cardNumber"
                            formname="moovCardForm"
                            placeholder="Enter Card Number"
                          ></moov-card-number-input>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>CVV</td>
                    <td>
                      <div className='formInput'>
                        <div className='formInputMoov'>
                          <moov-card-security-code-input
                            ref={cardCvcRef}
                            name="cardCvv"
                            formname="moovCardForm"
                            placeholder="Enter CVC"
                          ></moov-card-security-code-input>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>Expiry Date</td>
                    <td>
                      <div className='formInput'>
                        <div className='formInputMoov'>
                          <moov-expiration-date-input
                            ref={cardExpiryRef}
                            name="expiration"
                            formname="moovCardForm"
                          ></moov-expiration-date-input>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>Billing ZIP</td>
                    <td>
                      <div className='formInput'>
                        <input
                          type='text'
                          name="postalCode"
                          placeholder='ZIP / Postal Code'
                          inputMode='numeric'
                          autoComplete='postal-code'
                          required />
                      </div>
                    </td>
                  </tr>
                </table>
                {/* Declarative Moov form element that owns tokenization/submission.
                    It self-initializes on mount (calling into Moov.js once loaded)
                    and backs requestSubmit(); the inputs above register to it via
                    their matching `formname`. Kept out of view but in the layout. */}
                <moov-form
                  ref={moovFormRef}
                  name="moovCardForm"
                  method="POST"
                  style={{ position: 'absolute', left: '-9999px', top: 0, width: '1px', height: '1px', overflow: 'hidden', pointerEvents: 'none' }}
                ></moov-form>
                <div className='rowFormInput'>
                  <label className='accountMinButton'>
                    <input
                      type='checkbox'
                      name='isPrimary'
                      onChange={(e) => setIsPrimary(e.target.checked)}
                      checked={isPrimary}
                    />
                    <span>Set as primary acccount</span>
                  </label>
                </div>
              </>
            ) : (
              <>
                <table className='formTable'>
                  <tr>
                    <td>Name on Card</td>
                    <td>
                      <div className='formInput'>
                        <input
                          onChange={handleInputChange}
                          type='text'
                          name="cardOwnerName"
                          value={isNew ? null : paymentDetails?.detail.cardOwnerName}
                          required />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>Card Number</td>
                    <td>
                      <div className='formInput'>
                        <input
                          onChange={handleInputChange}
                          type='text'
                          name="cardNumber"
                          placeholder='Enter Card Number'
                          inputMode='numeric'
                          value={isNew ? null : paymentDetails?.detail.cardNumber}
                          required/>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>CVV</td>
                    <td>
                      <div className='formInput'>
                        <input
                          type='password'
                          style={{maxWidth : 150, minWidth : 'auto', marginLeft : 0}}
                          name="cvv"
                          placeholder='Enter CVC'
                          inputMode='numeric'
                          required/>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>Expiry Date</td>
                    <td>
                      <div className='formInput'>
                        <input
                          onChange={handleInputChange}
                          type='text'
                          name="expiryMonth"
                          placeholder='MM'
                          inputMode='numeric'
                          maxLength={2}
                          value={isNew ? null : paymentDetails?.detail.expiryMonth}
                          required
                          style={{ flex : 'auto', maxWidth : 80, minWidth : 'auto', margin: 'unset', marginLeft : 0 }} />
                        <input
                          onChange={handleInputChange}
                          type='text'
                          name="expiryYear"
                          placeholder='YYYY'
                          inputMode='numeric'
                          maxLength={4}
                          value={isNew ? null : paymentDetails?.detail.expiryYear}
                          required
                          style={{ flex : 'auto', maxWidth : 150, minWidth : 'auto', margin: 'unset', marginLeft : 0 }} />
                      </div>
                    </td>
                  </tr>
                </table>
                <div className='rowFormInput'>
                  {
                    ((!isNew && edit) || (isNew && edit)) &&
                    <label className='accountMinButton'>
                      <input type='checkbox' name='isPrimary' onChange={handleInputChange} checked={paymentDetails?.isPrimary} />
                      <span>Set as primary acccount</span>
                    </label>
                  }
                </div>
              </>
            )}
            <div className='message'>Sample Text</div>
            <div className="referenceImage" style={{ visibility: 'hidden' }}>
              <img src={image} alt='Reference' />
            </div>
            {
              (!isNew && edit) &&
              <label className='accountMinButton' onClick={() => deleteAccountConfirm(true)}>
                <MdDelete color='red' size={15} />
                <label style={{ color: 'red' }}>Delete Account</label>
              </label>
            }
            <div className='buttonContainer'>
              <Button
                className='minButton'
                margin='-5px'
                type='button'
                primary={false}
                onPressFunction={cancel}
              >Cancel</Button>

              <Button
                className='minButton'
                margin='-5px'
                type='submit'
                disable={isNewCard && !moovFormReady}
              >{isNewCard && !moovFormReady ? "Loading..." : "Submit"}</Button>
            </div>
          </form>
        </PopupWrapper>
      }
      {
        isDeleteCalled &&
        <PopupWrapper onClose={() => { deleteAccountConfirm(false) }}>
          <div style={{ fontSize: 16, textAlign: 'center' }}>
            Are you sure you want to delete this account?
          </div>
          <div style={{ width: 'fit-content', display: 'flex' }}>
            <Button
              className='minButton'
              type='button'
              primary={false}
              onPressFunction={() => { deleteAccountConfirm(false) }}
            >Cancel</Button>
            <Button
              className='minButton'
              type='submit'
              primary={true}
              onPressFunction={deleteAccountMethod}
            >Delete</Button>
          </div>
        </PopupWrapper>
      }
      {
        error && <PopupWrapper maxWidth={500} onClose={() => { setError(false) }}>
          <div style={{ fontSize: 16, textAlign: 'left', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <img src={ErrorPng} height={70} />
            Looks like this is your primary account to send payments, please add other primary account before you delete this.
          </div>
        </PopupWrapper>
      }
    </CustomWrapper>
  )
}

export default PaymentMethod